import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Paper, Typography, Box, Card, CardContent, Avatar, List, ListItem, ListItemIcon, ListItemText, Alert, Chip } from '@mui/material';
import { Doughnut, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import FolderIcon from '@mui/icons-material/Folder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import GroupIcon from '@mui/icons-material/Group';
import WarningIcon from '@mui/icons-material/Warning';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import EventIcon from '@mui/icons-material/Event';
import axios from 'axios';
import Loading from '../components/Loading';
import API_URL from '../config';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  
  useEffect(() => {
    const fetchStats = async () => {
      const token = localStorage.getItem('token');
      try {
        const res = await axios.get(`${API_URL}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchStats();
  }, []);

  if (!stats) return <Loading message="Loading Dashboard..." />;

  // Doughnut Data
  const doughnutData = {
    labels: ['At Risk', 'Completed', 'On Hold', 'On Track'],
    datasets: [
      {
        data: [
            stats.status_distribution['Overdue'] || 0, 
            stats.status_distribution['Completed'] || 0, 
            stats.status_distribution['Submitted'] || 0, 
            stats.status_distribution['In Progress'] || 0
        ],
        backgroundColor: ['#FFB020', '#6C757D', '#DC3545', '#00C292'],
        borderWidth: 0,
      },
    ],
  };

  // Bar Data (Dynamic)
  const kpiLabels = Object.keys(stats.kpi_performance);
  const kpiValues = Object.values(stats.kpi_performance);

  const barData = {
    labels: kpiLabels.length > 0 ? kpiLabels : ['No Data'],
    datasets: [
      {
        label: 'Score',
        data: kpiValues.length > 0 ? kpiValues : [0],
        backgroundColor: '#3B82F6',
        borderRadius: 5,
        barThickness: 30,
      },
    ],
  };

  // Budget vs Spent Data (Active Projects)
  const financialLabels = stats.project_financials ? stats.project_financials.map(p => p.name) : [];
  const budgetValues = stats.project_financials ? stats.project_financials.map(p => p.budget) : [];
  const spentValues = stats.project_financials ? stats.project_financials.map(p => p.spent) : [];

  const budgetData = {
    labels: financialLabels.length > 0 ? financialLabels : ['No Active Projects'],
    datasets: [
      {
        label: 'Budget',
        data: budgetValues.length > 0 ? budgetValues : [0],
        backgroundColor: '#00C292',
        borderRadius: 5,
        barThickness: 20,
      },
      {
        label: 'Spent',
        data: spentValues.length > 0 ? spentValues : [0],
        backgroundColor: '#DC3545',
        borderRadius: 5,
        barThickness: 20,
      },
    ],
  };

  const StatCard = ({ title, value, subtitle, icon, color, bg }) => (
    <Card sx={{ height: '100%', boxShadow: 1 }}>
      <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography color="textSecondary" variant="body2">{title}</Typography>
          <Typography variant="h4" sx={{ fontWeight: 'bold', my: 1 }}>{value}</Typography>
          <Typography variant="caption" color="textSecondary">{subtitle}</Typography>
        </Box>
        <Avatar sx={{ bgcolor: bg, color: color, width: 56, height: 56 }}>
          {icon}
        </Avatar>
      </CardContent>
    </Card>
  );

  return (
    <Box>

      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <StatCard 
            title="Your Projects" 
            value={stats.total_projects} 
            subtitle="Total projects assigned" 
            icon={<FolderIcon />} 
            color="#3B82F6" 
            bg="#EBF5FF" 
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard 
            title="Active Projects" 
            value={stats.active_projects} 
            subtitle="Currently in progress" 
            icon={<CheckCircleIcon />} 
            color="#00C292" 
            bg="#E6FFFA" 
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard 
            title="At Risk" 
            value={stats.at_risk} 
            subtitle="Projects needing attention" 
            icon={<InfoIcon />} 
            color="#FFB020" 
            bg="#FFF8E1" 
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard 
            title="Team Members" 
            value={stats.team_members} 
            subtitle={`${stats.utilization}% avg. utilization`} 
            icon={<GroupIcon />} 
            color="#6366F1" 
            bg="#EEF2FF" 
          />
        </Grid>
      </Grid>

      {/* Alerts Section */}
      {stats.alerts && stats.alerts.length > 0 && (
        <Box sx={{ mb: 4 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>You have {stats.alerts.length} projects at risk!</Alert>
            <Grid container spacing={2}>
                {stats.alerts.map(alert => (
                    <Grid item xs={12} md={6} key={alert.id}>
                        <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', borderLeft: '4px solid #FFB020' }}>
                            <WarningIcon color="warning" sx={{ mr: 2 }} />
                            <Box>
                                <Typography variant="subtitle1">{alert.message}</Typography>
                                <Typography variant="caption" color="textSecondary">Due: {alert.date}</Typography>
                            </Box>
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </Box>
      )}

      {/* My Tickets Section */}
      {stats.my_tickets && stats.my_tickets.length > 0 && (
        <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>My Active Tickets</Typography>
            <Grid container spacing={2}>
                {stats.my_tickets.map(ticket => (
                    <Grid item xs={12} md={4} key={ticket.id}>
                        <Paper 
                            sx={{ p: 2, borderLeft: '4px solid #3B82F6', cursor: 'pointer' }}
                            onClick={() => navigate(`/tickets/${ticket.id}`)}
                        >
                           
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ConfirmationNumberIcon color="primary" fontSize="small" />
                                    <Typography variant="subtitle2" fontWeight="bold">
                                       

                                          {`ACTS - T - ${String(ticket.id).padStart(5, '0')}`}
                                    </Typography>
                                </Box>
                               
                                <Chip label={ticket.status} size="small" color={ticket.status === 'In Progress' ? 'info' : 'warning'} />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              
                                    <Typography variant="subtitle2" >
                                      
 {ticket.title}
                                    </Typography>
                                </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                                <Typography variant="caption" color="textSecondary">{ticket.type}</Typography>
                                <Typography variant="caption" color="textSecondary">{ticket.date}</Typography>
                            </Box>
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </Box>
      )}

      {/* Weekly Schedule Section */}
      {stats.weekly_schedule && stats.weekly_schedule.length > 0 && (
        <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>My Weekly Schedule</Typography>
            <Grid container spacing={2}>
                {stats.weekly_schedule.map(entry => (
                    <Grid item xs={12} md={4} key={entry.id}>
                        <Paper 
                            sx={{ p: 2, borderLeft: '4px solid #00C292', cursor: 'pointer' }}
                            onClick={() => navigate(`/datasheets?editId=${entry.id}`)}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <EventIcon color="primary" fontSize="small" />
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        {entry.title}
                                    </Typography>
                                </Box>
                                <Chip label={entry.status} size="small" color={entry.status === 'Completed' ? 'success' : (entry.status === 'In Progress' ? 'info' : 'default')} />
                            </Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {entry.project_name} - {entry.type_of_work}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                                <Typography variant="caption" color="textSecondary">{entry.start_time}</Typography>
                                <Typography variant="caption" color="textSecondary">{entry.end_time || 'Ongoing'}</Typography>
                            </Box>
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Project Status Distribution</Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>Current status of your projects</Typography>
            <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Doughnut data={doughnutData} options={{ cutout: '70%', plugins: { legend: { position: 'bottom' } } }} />
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>KPI Performance</Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>Average scores across all categories</Typography>
            <Box sx={{ height: 300 }}>
                <Bar data={barData} options={{ 
                    responsive: true, 
                    maintainAspectRatio: false,
                    scales: { y: { beginAtZero: true, max: 100, grid: { borderDash: [5, 5] } }, x: { grid: { display: false } } },
                    plugins: { legend: { display: false } }
                }} />
            </Box>
          </Paper>
        </Grid>

        {/* Budget vs Spent Chart */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Project Financials</Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>Budget vs Spent for all active projects (excluding completed)</Typography>
            <Box sx={{ height: 300 }}>
              {financialLabels.length > 0 ? (
                <Bar data={budgetData} options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: { y: { beginAtZero: true, grid: { borderDash: [5, 5] } }, x: { grid: { display: false } } },
                  plugins: { legend: { position: 'top' } }
                }} />
              ) : (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="textSecondary">No active projects found.</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
